/**
 * Einstellungen der Hausverwaltung.
 *
 * Drei Dinge: der Name, der iDWELL-Schlüssel, die Menüpunkte der
 * Unterlagen — und die Mitarbeiter, die hier arbeiten dürfen.
 *
 * Die Menüpunkte sind der Adminbereich, den die Anforderung meint: Jede
 * Hausverwaltung legt fest, unter welchen Namen sie dem Beirat welche
 * Unterlagen zu welchem Prüfschritt vorlegt — „Journal statt Kontoauszug",
 * eine „Nebenkostenabrechnung" dazu, etwas ausblenden, was sie nie hat.
 */

import { useEffect, useState } from 'react';

import { api, datum } from '../../lib/api';
import type { Kategorie, Konto, Mitarbeiter } from '../../lib/typen';

const SCHRITT_NAMEN: Record<number, string> = {
  1: 'Kontenabgleich',
  2: 'Gesamtabrechnung',
  3: 'Kontenblätter und Belege',
  4: 'Abrechnungsergebnis',
  5: 'Wirtschaftsplan',
};

export function Einstellungen({ konto, onMeldung, onGeaendert }: { konto: Konto; onMeldung: (t: string) => void; onGeaendert: () => void }) {
  const [kategorien, setKategorien] = useState<Kategorie[]>([]);
  const [benutzer, setBenutzer] = useState<Mitarbeiter[]>([]);
  const [name, setName] = useState(konto.mandant?.name ?? '');
  const [kurzname, setKurzname] = useState(konto.mandant?.kurzname ?? '');
  const [apiKey, setApiKey] = useState('');
  const [laeuft, setLaeuft] = useState<string | null>(null);
  const [neueKategorie, setNeueKategorie] = useState<{ schritt: number; bezeichnung: string; hinweis: string; pflicht: boolean } | null>(null);
  const [bearbeitet, setBearbeitet] = useState<Kategorie | null>(null);
  const [neuerBenutzer, setNeuerBenutzer] = useState({ email: '', name: '' });
  const [zugangslink, setZugangslink] = useState<{ fuer: string; link: string; meldung: string } | null>(null);

  useEffect(() => {
    api.mandant
      .lesen()
      .then((a) => {
        setKategorien(a.kategorien);
        setBenutzer(a.benutzer);
        setName(a.mandant.name);
        setKurzname(a.mandant.kurzname ?? '');
      })
      .catch((g) => onMeldung((g as Error).message));
  }, [onMeldung]);

  async function nameSpeichern() {
    setLaeuft('name');
    try {
      await api.mandant.aendern({ name: name.trim(), kurzname: kurzname.trim() || null });
      onMeldung('Gespeichert.');
      onGeaendert();
    } catch (g) {
      onMeldung((g as Error).message);
    } finally {
      setLaeuft(null);
    }
  }

  async function schluesselSpeichern() {
    if (!apiKey.trim()) return;
    setLaeuft('key');
    try {
      const a = await api.mandant.idwellSetzen(apiKey.trim());
      setApiKey('');
      onMeldung(`Verbunden mit iDWELL: ${a.verbunden.mandant}.`);
      onGeaendert();
    } catch (g) {
      onMeldung((g as Error).message);
    } finally {
      setLaeuft(null);
    }
  }

  async function schluesselPruefen() {
    setLaeuft('pruefen');
    try {
      const a = await api.mandant.idwellPruefen();
      onMeldung(`Verbindung in Ordnung: ${a.mandant}${a.buchhaltung ? ' (mit Buchhaltung)' : ''}.`);
    } catch (g) {
      onMeldung((g as Error).message);
    } finally {
      setLaeuft(null);
    }
  }

  async function schluesselLoeschen() {
    if (!window.confirm('Den iDWELL-Schlüssel entfernen? Neue Prüfungen können dann nur noch aus Importpaketen entstehen.')) return;
    try {
      await api.mandant.idwellLoeschen();
      onMeldung('Schlüssel entfernt.');
      onGeaendert();
    } catch (g) {
      onMeldung((g as Error).message);
    }
  }

  async function kategorieSpeichern(w: { schritt: number; bezeichnung: string; hinweis: string; pflicht: boolean }) {
    if (!w.bezeichnung.trim()) return;
    try {
      const k = await api.mandant.kategorieAnlegen({
        bezeichnung: w.bezeichnung.trim(),
        hinweis: w.hinweis.trim() || undefined,
        schritt: w.schritt,
        pflicht: w.pflicht,
      });
      setKategorien((alt) => [...alt, k]);
      setNeueKategorie(null);
      onMeldung(`„${k.bezeichnung}" angelegt.`);
    } catch (g) {
      onMeldung((g as Error).message);
    }
  }

  async function kategorieAendern(id: string, daten: Parameters<typeof api.mandant.kategorieAendern>[1]) {
    try {
      const k = await api.mandant.kategorieAendern(id, daten);
      setKategorien((alt) => alt.map((x) => (x.id === id ? k : x)));
      return true;
    } catch (g) {
      onMeldung((g as Error).message);
      return false;
    }
  }

  async function kategorieLoeschen(k: Kategorie) {
    if (!window.confirm(`„${k.bezeichnung}" löschen?`)) return;
    try {
      await api.mandant.kategorieLoeschen(k.id);
      setKategorien((alt) => alt.filter((x) => x.id !== k.id));
    } catch (g) {
      onMeldung((g as Error).message);
    }
  }

  async function verschieben(k: Kategorie, richtung: -1 | 1) {
    const gleicherSchritt = kategorien.filter((x) => x.schritt === k.schritt).sort((a, b) => a.reihenfolge - b.reihenfolge);
    const i = gleicherSchritt.findIndex((x) => x.id === k.id);
    const j = i + richtung;
    if (j < 0 || j >= gleicherSchritt.length) return;
    const neu = [...gleicherSchritt];
    [neu[i], neu[j]] = [neu[j], neu[i]];
    try {
      const alle = await api.mandant.kategorienOrdnen(neu.map((x) => x.id));
      setKategorien(alle);
    } catch (g) {
      onMeldung((g as Error).message);
    }
  }

  async function benutzerEinladen(e: React.FormEvent) {
    e.preventDefault();
    setLaeuft('benutzer');
    try {
      const a = await api.mandant.benutzerEinladen(neuerBenutzer.email.trim(), neuerBenutzer.name.trim());
      setZugangslink({ fuer: neuerBenutzer.email.trim(), link: a.link, meldung: a.meldung });
      setNeuerBenutzer({ email: '', name: '' });
      setBenutzer(await api.mandant.benutzer());
    } catch (g) {
      onMeldung((g as Error).message);
    } finally {
      setLaeuft(null);
    }
  }

  async function neuerLink(b: Mitarbeiter) {
    try {
      const a = await api.mandant.benutzerLink(b.id);
      setZugangslink({ fuer: b.email, link: a.link, meldung: a.meldung });
    } catch (g) {
      onMeldung((g as Error).message);
    }
  }

  const mandant = konto.mandant!;

  return (
    <div className="wrap" style={{ maxWidth: 960 }}>
      <div className="seitenkopf">
        <div>
          <h2 style={{ margin: 0 }}>Einstellungen</h2>
          <p className="s" style={{ margin: '4px 0 0' }}>Hausverwaltung, iDWELL-Verbindung, Menüpunkte der Unterlagen, Mitarbeiter.</p>
        </div>
      </div>

      <h3 className="sec">Hausverwaltung</h3>
      <div className="card" style={{ padding: 16 }}>
        <div className="feldreihe">
          <label className="feld">
            <span>Name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="feld">
            <span>Kurzname (für die Beiratsansicht)</span>
            <input value={kurzname} onChange={(e) => setKurzname(e.target.value)} placeholder="optional" />
          </label>
        </div>
        <button className="btn sm" onClick={nameSpeichern} disabled={laeuft === 'name' || !name.trim()}>
          Speichern
        </button>
      </div>

      <h3 className="sec">iDWELL-Verbindung</h3>
      <div className="card" style={{ padding: 16 }}>
        {mandant.idwellVerbunden ? (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
            <span className="pill ok"><span className="dot" />Schlüssel hinterlegt{mandant.schluesselSeit ? ` seit ${datum(mandant.schluesselSeit)}` : ''}</span>
            <button className="btn ghost sm" onClick={schluesselPruefen} disabled={laeuft === 'pruefen'}>Verbindung prüfen</button>
            <button className="btn ghost sm" onClick={schluesselLoeschen}>Entfernen</button>
          </div>
        ) : (
          <p className="s" style={{ marginTop: 0 }}>
            Kein Schlüssel hinterlegt. Ohne iDWELL kommen Belege und Buchungen aus einem Importpaket (Buchungsliste + Belegordner).
          </p>
        )}
        <label className="feld">
          <span>{mandant.idwellVerbunden ? 'Schlüssel tauschen' : 'API-Schlüssel aus iDWELL'}</span>
          <input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="eyJ…" autoComplete="off" />
          <small>Wird vor dem Speichern gegen iDWELL geprüft und verschlüsselt abgelegt. Er wird nie wieder angezeigt.</small>
        </label>
        <button className="btn sm" onClick={schluesselSpeichern} disabled={laeuft === 'key' || !apiKey.trim()}>
          {laeuft === 'key' ? 'Wird geprüft …' : 'Schlüssel hinterlegen'}
        </button>
      </div>

      <div className="seitenkopf" style={{ marginTop: 22 }}>
        <h3 className="sec" style={{ margin: 0 }}>Menüpunkte der Unterlagen</h3>
        <div className="spacer" />
        <button className="btn primary sm" onClick={() => setNeueKategorie({ schritt: 2, bezeichnung: '', hinweis: '', pflicht: false })}>
          + Menüpunkt
        </button>
      </div>
      <p className="s" style={{ marginTop: 0 }}>
        Unter diesen Punkten stellen Sie dem Beirat Unterlagen bereit, je Prüfschritt. Umbenennen, einem anderen Schritt zuordnen,
        ausblenden oder ergänzen — zum Beispiel „Journal" statt „Kontoauszug" oder eine „Nebenkostenabrechnung".
      </p>

      {[1, 2, 3, 4, 5].map((nr) => (
        <div key={nr} className="card" style={{ padding: 14, marginBottom: 10 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 6 }}>
            <div className="schritt-nummer offen" aria-hidden="true">{nr}</div>
            <b>{SCHRITT_NAMEN[nr]}</b>
          </div>
          {kategorien
            .filter((k) => k.schritt === nr)
            .sort((a, b) => a.reihenfolge - b.reihenfolge)
            .map((k) => (
              <div key={k.id} className={`kategorie-zeile${k.aktiv ? '' : ' inaktiv'}`}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <b>{k.bezeichnung}</b>
                  {!k.aktiv && <span className="chip" style={{ marginLeft: 6 }}>ausgeblendet</span>}
                  {k.hinweis && <div className="s" style={{ margin: 0 }}>{k.hinweis}</div>}
                </div>
                <button className="iconbtn" aria-label="Nach oben" onClick={() => verschieben(k, -1)}>↑</button>
                <button className="iconbtn" aria-label="Nach unten" onClick={() => verschieben(k, 1)}>↓</button>
                <button className="btn ghost sm" onClick={() => setBearbeitet(k)}>Bearbeiten</button>
                <button className="btn ghost sm" onClick={() => kategorieAendern(k.id, { aktiv: !k.aktiv })}>
                  {k.aktiv ? 'Ausblenden' : 'Einblenden'}
                </button>
                <button className="btn ghost sm" onClick={() => kategorieLoeschen(k)}>Löschen</button>
              </div>
            ))}
        </div>
      ))}

      <h3 className="sec" style={{ marginTop: 22 }}>Mitarbeiter</h3>
      <div className="card" style={{ padding: 16 }}>
        <table className="schlicht">
          <tbody>
            {benutzer.map((b) => (
              <tr key={b.id}>
                <td><b>{b.name}</b></td>
                <td className="s">{b.email}</td>
                <td className="s">{b.passwortGesetzt ? (b.letzteAnmeldungAm ? `zuletzt ${datum(b.letzteAnmeldungAm)}` : 'noch nie angemeldet') : 'Passwort noch nicht gesetzt'}</td>
                <td className="s">{b.aktiv ? '' : 'gesperrt'}</td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button className="btn ghost sm" onClick={() => neuerLink(b)}>Neuer Zugangslink</button>
                  {b.id !== konto.benutzer.id && (
                    <button className="btn ghost sm" onClick={async () => setBenutzer(await api.mandant.benutzerAendern(b.id, { aktiv: !b.aktiv }))}>
                      {b.aktiv ? 'Sperren' : 'Freischalten'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <form onSubmit={benutzerEinladen} style={{ marginTop: 14, borderTop: '1px solid var(--line)', paddingTop: 14 }}>
          <div className="feldreihe">
            <label className="feld">
              <span>E-Mail</span>
              <input type="email" required value={neuerBenutzer.email} onChange={(e) => setNeuerBenutzer((a) => ({ ...a, email: e.target.value }))} />
            </label>
            <label className="feld">
              <span>Name</span>
              <input value={neuerBenutzer.name} onChange={(e) => setNeuerBenutzer((a) => ({ ...a, name: e.target.value }))} />
            </label>
          </div>
          <button className="btn sm" type="submit" disabled={laeuft === 'benutzer'}>Mitarbeiter einladen</button>
          <span className="s" style={{ marginLeft: 10 }}>
            {konto.mailBereit ? 'Der Zugangslink wird per Mail verschickt.' : 'Kein Mailversand eingerichtet — der Link erscheint hier zum Weitergeben.'}
          </span>
        </form>
        {zugangslink && <Linkkasten fuer={zugangslink.fuer} link={zugangslink.link} meldung={zugangslink.meldung} onZu={() => setZugangslink(null)} />}
      </div>

      {(neueKategorie || bearbeitet) && (
        <div className="ovl" onMouseDown={(e) => e.target === e.currentTarget && (setNeueKategorie(null), setBearbeitet(null))}>
          <div className="sheet">
            <h3>{bearbeitet ? 'Menüpunkt bearbeiten' : 'Neuer Menüpunkt'}</h3>
            <KategorieFormular
              start={bearbeitet ? { schritt: bearbeitet.schritt, bezeichnung: bearbeitet.bezeichnung, hinweis: bearbeitet.hinweis ?? '', pflicht: bearbeitet.pflicht } : neueKategorie!}
              onAbbrechen={() => { setNeueKategorie(null); setBearbeitet(null); }}
              onSpeichern={async (w) => {
                if (bearbeitet) {
                  if (await kategorieAendern(bearbeitet.id, { bezeichnung: w.bezeichnung, hinweis: w.hinweis || null, schritt: w.schritt, pflicht: w.pflicht })) setBearbeitet(null);
                } else {
                  await kategorieSpeichern(w);
                }
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function KategorieFormular({
  start,
  onAbbrechen,
  onSpeichern,
}: {
  start: { schritt: number; bezeichnung: string; hinweis: string; pflicht: boolean };
  onAbbrechen: () => void;
  onSpeichern: (w: { schritt: number; bezeichnung: string; hinweis: string; pflicht: boolean }) => void;
}) {
  const [w, setW] = useState(start);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSpeichern(w);
      }}
    >
      <label className="feld">
        <span>Bezeichnung</span>
        <input value={w.bezeichnung} onChange={(e) => setW((a) => ({ ...a, bezeichnung: e.target.value }))} required autoFocus placeholder="z. B. Nebenkostenabrechnung" />
      </label>
      <label className="feld">
        <span>Prüfschritt</span>
        <select value={w.schritt} onChange={(e) => setW((a) => ({ ...a, schritt: Number(e.target.value) }))}>
          {[1, 2, 3, 4, 5].map((n) => (
            <option key={n} value={n}>
              {n}. {SCHRITT_NAMEN[n]}
            </option>
          ))}
        </select>
      </label>
      <label className="feld">
        <span>Hinweis für den Beirat (optional)</span>
        <input value={w.hinweis} onChange={(e) => setW((a) => ({ ...a, hinweis: e.target.value }))} placeholder="Worauf beim Lesen zu achten ist" />
      </label>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
        <button className="btn ghost" type="button" onClick={onAbbrechen}>Abbrechen</button>
        <button className="btn primary" type="submit" disabled={!w.bezeichnung.trim()}>Speichern</button>
      </div>
    </form>
  );
}

export function Linkkasten({ fuer, link, meldung, onZu }: { fuer: string; link: string; meldung: string; onZu: () => void }) {
  const [kopiert, setKopiert] = useState(false);
  return (
    <div className="linkkasten">
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <b>Zugangslink für {fuer}</b>
        <span className="s" style={{ margin: 0 }}>{meldung}</span>
        <div className="spacer" />
        <button className="iconbtn" aria-label="Schließen" onClick={onZu}>✕</button>
      </div>
      <div className="mono linktext">{link}</div>
      <button
        className="btn ghost sm"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(link);
            setKopiert(true);
            setTimeout(() => setKopiert(false), 2000);
          } catch {
            setKopiert(false);
          }
        }}
      >
        {kopiert ? 'Kopiert' : 'Link kopieren'}
      </button>
      <p className="note" style={{ marginBottom: 0 }}>Gilt 48 Stunden und nur einmal. Danach lässt sich jederzeit ein neuer erzeugen.</p>
    </div>
  );
}
